import AV from '../config/leancloud';

export const ROLE_LEVELS = {
  ADMIN: 4,    // 管理员
  FINANCE: 3,  // 财务
  MANAGER: 2,  // 主管
  EMPLOYEE: 1  // 员工
};

export const ROLES = {
  ADMIN: 'admin',
  FINANCE: 'finance',
  MANAGER: 'manager',
  EMPLOYEE: 'employee'
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: '管理员',
  [ROLES.FINANCE]: '财务',
  [ROLES.MANAGER]: '主管',
  [ROLES.EMPLOYEE]: '员工'
};

// Add these constants at the top of the file
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

export const authService = {
  roleCache: new Map(), // Add role cache
  roleCacheExpiry: 5 * 60 * 1000, // 5 minutes cache expiry
  lastRoleCacheUpdate: 0,

  async testAdminAccess() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        console.log('No user logged in');
        return false;
      }
      
      console.log('Current user:', currentUser.get('username'));
      const isAdmin = await this.hasRole(currentUser, 'admin');
      console.log('Is admin?', isAdmin);
      return isAdmin;
    } catch (error) {
      console.error('Error testing admin access:', error);
      return false;
    }
  },

  // Register new user
  async register(username, password, email = null, role = ROLES.EMPLOYEE) {
    try {
      // Store current user before registration
      const currentUser = this.getCurrentUser();
      
      // Ensure roles exist first
      await this.ensureRolesExist();
      
      const user = new AV.User();
      user.setUsername(username);
      user.setPassword(password);
      if (email) {
        user.setEmail(email);
      }
      
      // Use regular signUp with master key
      const newUser = await user.signUp(null, { useMasterKey: true });
      
      // Find and assign role
      const roleQuery = new AV.Query(AV.Role);
      roleQuery.equalTo('name', role);
      let roleObject = await roleQuery.first({ useMasterKey: true });
      
      if (!roleObject) {
        throw new Error(`Role ${role} not found`);
      }
      
      roleObject.getUsers().add(newUser);
      await roleObject.save(null, { useMasterKey: true });

      // If there was a current user, make sure they stay logged in
      if (currentUser) {
        await AV.User.become(currentUser._sessionToken);
      }
      
      return newUser;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  },

  // Login
  async login(username, password, rememberMe = false) {
    try {
      const user = await AV.User.logIn(username, password);
      
      // Set tokens with appropriate expiry
      this.setTokens(
        user.getSessionToken(),
        rememberMe ? 604800 : 7200 // 7 days if remember me, else 2 hours
      );

      return user;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  },

  // Logout
  async logout() {
    try {
      await AV.User.logOut();
      this.clearTokens();
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  },

  // Get current user
  getCurrentUser() {
    if (this.isTokenExpired()) {
      this.clearTokens();
      return null;
    }
    return AV.User.current();
  },

  // Check if user has role
  async hasRole(user, roleName) {
    if (!user) return false;
    
    const roleQuery = new AV.Query(AV.Role);
    roleQuery.equalTo('name', roleName);
    roleQuery.equalTo('users', user);
    
    const count = await roleQuery.count();
    return count > 0;
  },

  // Get user's roles
  async getUserRoles(user) {
    try {
      const roleQuery = new AV.Query('_Role');
      roleQuery.equalTo('users', user);
      const roles = await roleQuery.find();
      console.log('Found roles:', roles);
      return roles;
    } catch (error) {
      console.error('Error getting user roles:', error);
      throw error;
    }
  },

  // Update user
  async updateUser(user, data) {
    try {
      if (data.email) {
        user.setEmail(data.email);
      }
      
      // Update roles
      if (data.roles) {
        // Remove from old roles
        const currentRoles = await this.getUserRoles(user);
        for (const role of currentRoles) {
          role.getUsers().remove(user);
          await role.save();
        }
        
        // Add to new roles
        for (const roleName of data.roles) {
          const roleQuery = new AV.Query(AV.Role);
          roleQuery.equalTo('name', roleName);
          let role = await roleQuery.first();
          
          if (!role) {
            const roleACL = new AV.ACL();
            roleACL.setPublicReadAccess(true);
            role = new AV.Role(roleName, roleACL);
            await role.save();
          }
          
          role.getUsers().add(user);
          await role.save();
        }
      }
      
      await user.save();
      return user;
    } catch (error) {
      throw error;
    }
  },

  // Reset password
  async resetPassword(username, newPassword) {
    try {
      await AV.User.requestPasswordReset(username);
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Add this method to create all roles
  async initializeRoles() {
    try {
      for (const roleName of Object.values(ROLES)) {
        const roleQuery = new AV.Query(AV.Role);
        roleQuery.equalTo('name', roleName);
        let roleObject = await roleQuery.first({ useMasterKey: true });
        
        if (!roleObject) {
          // Create role if it doesn't exist
          const roleACL = new AV.ACL();
          roleACL.setPublicReadAccess(true);
          roleACL.setRoleWriteAccess('admin', true);
          
          const role = new AV.Role(roleName, roleACL);
          await role.save(null, { useMasterKey: true });
          console.log(`Created role: ${roleName}`);
        }
      }
      return true;
    } catch (error) {
      console.error('Error initializing roles:', error);
      throw error;
    }
  },

  // Add this method to authService
  async deleteUser(userId) {
    try {
      // Check if current user is admin
      const currentUser = this.getCurrentUser();
      const isAdmin = await this.hasRole(currentUser, ROLES.ADMIN);
      
      if (!isAdmin) {
        throw new Error('只有管理员可以删除用户');
      }

      const user = AV.Object.createWithoutData('_User', userId);
      await user.destroy({ useMasterKey: true });
      return true;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  },

  // Add to authService
  async updatePassword(oldPassword, newPassword) {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('未登录');
      }
      
      // Verify old password by trying to login
      await AV.User.logIn(user.get('username'), oldPassword);
      
      // Update to new password
      user.setPassword(newPassword);
      await user.save(null, { useMasterKey: true });
      
      return true;
    } catch (error) {
      if (error.code === 210) {
        throw new Error('当前密码错误');
      }
      throw error;
    }
  },

  // Update the resetPassword method
  async resetUserPassword(userId, newPassword) {
    try {
      // Check if current user is admin
      const currentUser = this.getCurrentUser();
      const isAdmin = await this.hasRole(currentUser, ROLES.ADMIN);
      
      if (!isAdmin) {
        throw new Error('只有管理员可以重置密码');
      }

      const user = AV.Object.createWithoutData('_User', userId);
      user.setPassword(newPassword);
      await user.save(null, { useMasterKey: true });
      
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  },

  // Add method to get user's role level
  async getUserLevel(user) {
    if (!user) return 0;
    const roles = await this.getUserRoles(user);
    if (roles.length === 0) return 0;
    return ROLE_LEVELS[roles[0].get('name')] || 0;
  },

  // Add method to check if user can access certain level
  async canAccess(requiredLevel) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        console.log('No current user found');
        return false;
      }

      // Get user's roles if not already loaded
      const roles = currentUser.roles || await this.getUserRoles(currentUser);
      console.log('User roles for permission check:', roles);

      // Map roles to their levels
      const userLevels = roles.map(role => {
        switch (role.get('name')) {
          case 'admin': return ROLE_LEVELS.ADMIN;
          case 'manager': return ROLE_LEVELS.MANAGER;
          case 'finance': return ROLE_LEVELS.FINANCE;
          default: return ROLE_LEVELS.USER;
        }
      });

      console.log('User levels:', userLevels);
      console.log('Required level:', requiredLevel);

      // User can access if they have any role with sufficient level
      const hasAccess = userLevels.some(level => level >= requiredLevel);
      console.log('Access result:', hasAccess);

      return hasAccess;
    } catch (error) {
      console.error('Error checking access:', error);
      return false;
    }
  },

  // Add this method to check and create roles if they don't exist
  async ensureRolesExist() {
    try {
      for (const roleName of Object.values(ROLES)) {
        const roleQuery = new AV.Query(AV.Role);
        roleQuery.equalTo('name', roleName);
        let roleObject = await roleQuery.first({ useMasterKey: true });
        
        if (!roleObject) {
          console.log(`Creating role: ${roleName}`);
          const roleACL = new AV.ACL();
          roleACL.setPublicReadAccess(true);
          
          const role = new AV.Role(roleName, roleACL);
          await role.save(null, { useMasterKey: true });
        }
      }
      return true;
    } catch (error) {
      console.error('Error ensuring roles exist:', error);
      throw error;
    }
  },

  clearRoleCache() {
    this.roleCache.clear();
    this.lastRoleCacheUpdate = 0;
  },

  // Call this after role changes
  async updateUserRoleCache(userId) {
    this.roleCache.delete(userId);
  },

  // Add these new methods
  setTokens(sessionToken, expiresIn = 7200) {
    localStorage.setItem(TOKEN_KEY, sessionToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, 
      new Date(Date.now() + expiresIn * 1000).toISOString()
    );
  },

  clearTokens() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  },

  isTokenExpired() {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return true;
    return new Date() > new Date(expiry);
  },
}; 